import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import Modal from '../../shared/components/modals/Modal';
import './DataImportPage.css';
import { API_ENDPOINTS } from '../../config/apiConfig';

/**
 * DataImportPage Component
 *
 * Role-based bulk data import/export module:
 *
 * SUPERADMIN: Can import/export all master data across:
 * - Device Names
 * - Departments
 * - Locations
 * - Complaint Categories
 * - Users
 * - Devices
 *
 * ADMIN (Location Admin): Can only import/export Users and Devices for their assigned location:
 * - Users (location-scoped)
 * - Devices (location-scoped, location_code cannot be modified)
 *
 * Features:
 * - Download role-specific template Excel file
 * - Upload master Excel file with multiple sheets
 * - Validate data before import
 * - Track import progress and results
 * - Support for insert/update operations
 * - For Devices: Use <NEW> keyword for new devices, actual device_identifier for updates
 */
function resolveOrganizationNameForDownload(user) {
  return (
    user?.organization_name
    || sessionStorage.getItem('organization_name')
    || user?.org_code
    || (user?.organization_id != null ? `ORG${user.organization_id}` : null)
    || 'MASTER'
  );
}

function DataImportPage({ token, user }) {
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [activeTab, setActiveTab] = useState('import'); // 'import' or 'export'
  const [previewData, setPreviewData] = useState(null); // Preview data with validation
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [guideTab, setGuideTab] = useState('overview'); // overview | rules | sheet name
  const [showImportModal, setShowImportModal] = useState(false); // Unified import modal
  const [importModalState, setImportModalState] = useState('file-selection'); // 'file-selection', 'progress', 'results'
  const [employeeIdMode, setEmployeeIdMode] = useState('manual'); // 'auto' or 'manual'
  const [selectedExportSheets, setSelectedExportSheets] = useState([]);

  // Fetch EMPLOYEE_ID_MODE from environment
  useEffect(() => {
    const mode = process.env.REACT_APP_EMPLOYEE_ID_MODE || 'manual';
    setEmployeeIdMode(mode.toLowerCase());
  }, []);

  // Role-available export sheets (must be computed before any conditional return — hooks rules)
  const availableExportSheets =
    user?.role === 'superadmin'
      ? ['Device Names', 'Departments', 'Locations', 'Complaint Categories', 'Users', 'Devices']
      : user?.role === 'admin'
        ? ['Users', 'Devices']
        : [];

  // Keep export selection in sync with role-available sheets (default: all selected)
  useEffect(() => {
    setSelectedExportSheets(availableExportSheets);
    // availableExportSheets is derived from role only; join keeps deps stable for identical lists
  }, [user?.role, availableExportSheets.join('|')]);

  // Validate user is superadmin or admin
  if (user?.role !== 'superadmin' && user?.role !== 'admin') {
    return (
      <div className="data-export-container">
        <div className="access-denied-banner">
          <span className="icon">🔒</span>
          <div>
            <h3>Access Denied</h3>
            <p>Only superadmins and location admins can access the Data Import module.</p>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Download template Excel file with proper structure and column headers
   * File is prefixed with organization name from organizations table
   */
  const handleDownloadTemplate = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        API_ENDPOINTS.DATA_IMPORT.DOWNLOAD_TEMPLATE,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache'
          },
          // Bust any intermediary/browser cache of this URL (must match server-generated role template)
          params: { _cb: Date.now() },
          responseType: 'blob'
        }
      );

      // Create blob and download with organization name prefix
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const orgName = resolveOrganizationNameForDownload(user);
      link.setAttribute('download', `${orgName}_master_data_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Template downloaded successfully!');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle file selection and upload
   */
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  /**
   * Get sheets available for the current user's role
   * Superadmin: All sheets (Device Names, Departments, Locations, Complaint Categories, Users, Devices)
   * Admin: Only Users and Devices (location-scoped)
   *
   * MODE-AWARE: Users sheet columns depend on EMPLOYEE_ID_MODE:
   * - AUTO mode (7 columns): username, email, full_name, contact_number, role, department_name, location_code
   * - MANUAL mode (8 columns): username, employee_id, email, full_name, contact_number, role, department_name, location_code
   * - department_name: one or more names comma-separated (e.g. Radiology, REHAB)
   */
  const getAvailableSheets = () => {
    // Build Users columns based on EMPLOYEE_ID_MODE
    const usersColumns = employeeIdMode === 'manual'
      ? ['username', 'employee_id', 'email', 'full_name', 'contact_number', 'role', 'department_name', 'location_code']
      : ['username', 'email', 'full_name', 'contact_number', 'role', 'department_name', 'location_code'];

    const devicesColumns = ['device_identifier', 'device_name', 'model', 'serial_number', 'year_of_manufacture', 'manufacturer', 'supplier', 'installation_date', 'product_cost', 'custodian_employee_id', 'department_name', 'location_code', 'physical_location', 'warranty_period', 'warranty_expiry', 'service_contract_type', 'service_contract_period', 'service_contract_cost', 'contract_start_date', 'contract_expiry', 'ppm_frequency', 'ppm_last_done_date', 'ppm_next_due_date', 'calibration_required', 'calibration_frequency', 'calibration_last_done_date', 'calibration_next_due_date', 'compliance_required', 'compliance_type', 'compliance_frequency', 'compliance_last_done_date', 'compliance_next_due_date', 'purchase_date', 'notes', 'primary_technician_employee_id', 'secondary_technician_employee_id'];

    const complaintCategoriesColumns = [
      'code',
      'label',
      'sort_order',
      'is_active',
      'applies_to_all_locations',
      'location_codes',
      'applies_to_all_device_names',
      'device_names',
    ];

    if (user?.role === 'superadmin') {
      return {
        'Device Names': ['name', 'code', 'category', 'description', 'criticality'],
        'Departments': ['name', 'code', 'icon', 'description'],
        'Locations': ['name', 'location_code', 'contact_email', 'contact_phone', 'address', 'city', 'state', 'country'],
        'Complaint Categories': complaintCategoriesColumns,
        'Users': usersColumns,
        'Devices': devicesColumns
      };
    } else if (user?.role === 'admin') {
      // Admin can only import/export Users and Devices
      return {
        'Users': usersColumns,
        'Devices': devicesColumns
      };
    }
    return {};
  };

  /**
   * Expected template sheets and their required columns
   */
  const EXPECTED_SHEETS = getAvailableSheets();

  /**
   * Get role-specific guide content for import instructions
   * Tailors the guidance based on user role capabilities and restrictions
   */
  const getRoleSpecificGuideContent = () => {
    if (user?.role === 'superadmin') {
      return {
        mainTitle: '📂 Master Data Management',
        mainDescription: 'As a System Administrator, you have full control over all organizational master data. Import data in the recommended order to maintain data integrity and relationships.',
        importFocusAreas: [
          'System-wide master data (Device Names, Departments, Locations, Complaint Categories)',
          'User accounts and role assignments across all locations',
          'Device inventory across the entire organization',
          'Organization-wide data consistency and validation'
        ],
        keyRestrictions: [],
        keyCapabilities: [
          'Import and manage all entity types',
          'Create new Departments, Locations, Device Names, and Complaint Categories',
          'Assign users to any location',
          'View and import data from all locations',
          'Update device location assignments'
        ],
        importOrderTips: 'For a full first-time load, import in this order so new references resolve in one file. Partial uploads are fine when dependencies already exist in the system.',
        importOrderSteps: ['Device Names', 'Departments', 'Locations', 'Complaint Categories', 'Users', 'Devices'],
        selectiveImportDeps: [
          {
            sheet: 'Device Names',
            dependsOn: 'None',
          },
          {
            sheet: 'Departments',
            dependsOn: 'None',
          },
          {
            sheet: 'Locations',
            dependsOn: 'None',
          },
          {
            sheet: 'Complaint Categories',
            dependsOn: 'None when applies-to-all is TRUE. If applicability is narrowed, location_codes / device_names must already exist (in this file or in the system).',
          },
          {
            sheet: 'Users',
            dependsOn: 'Departments and Locations must already exist (in this file or in the system).',
          },
          {
            sheet: 'Devices',
            dependsOn: 'Device Names, Departments, and Locations must already exist (in this file or in the system).',
          },
        ],
        locationNotes: 'Location codes are unique per organization. You can create new locations and assign devices/users to any location.',
        relationshipIntro: 'Import foundational sheets first. Duplicates are rejected; dates use YYYY-MM-DD. Open Detailed Reference for full field rules.',
        keyReminders: [
          {
            title: 'Uniqueness',
            items: [
              'Device Names / Departments: unique name and code',
              'Locations: unique location_code',
              'Complaint Categories: unique code',
              'Users: unique username, email, contact, employee_id',
              'Devices: unique serial_number (and device_identifier except new-device)',
            ],
          },
          {
            title: 'Users',
            items: [
              'New users need location_code',
              'department_name can be comma-separated (full list on update)',
              'employee_id cannot change after assignment',
              'Email change re-verifies; fix pending links / deactivated accounts in User Management first',
            ],
          },
          {
            title: 'Complaint Categories',
            items: [
              'Upsert by code; system Other is not exported and cannot be deactivated or narrowed via import',
              'When applies_to_all_locations is FALSE, location_codes is required (comma-separated)',
              'When applies_to_all_device_names is FALSE, device_names is required (comma-separated)',
            ],
          },
          {
            title: 'Devices',
            items: [
              'location_code cannot change after creation',
              'Custodian / technicians use unique employee IDs',
              'Technicians must be active and assigned to the device department and location',
            ],
          },
        ],
        rulesNote: 'Pre-check flags issues but does not block import. The server returns every issue per row. Use the template Instructions sheet for full formats.',
      };
    } else if (user?.role === 'admin') {
      return {
        mainTitle: '📊 Location Data Management',
        mainDescription: 'As a Location Administrator, you manage users and devices for your assigned location. All records are automatically scoped to your location.',
        importFocusAreas: [
          'Users for your assigned location',
          'Devices for your assigned location',
          'User role assignments within your location',
          'Device maintenance and compliance tracking'
        ],
        keyRestrictions: [
          'Cannot create or modify Departments',
          'Cannot create or modify Locations',
          'Cannot create or modify Device Names',
          'Cannot create or modify Complaint Categories',
          'Cannot change a device\'s location after creation',
          'All imported records are scoped to your assigned location'
        ],
        keyCapabilities: [
          'Add new users to your location',
          'Update existing user information',
          'Add new devices to your location',
          'Update existing device details',
          'Manage device maintenance schedules'
        ],
        importOrderTips: 'Import Users first, then Devices. Master data (departments, device names, locations) must already exist — contact your System Administrator if something is missing.',
        importOrderSteps: ['Users', 'Devices'],
        selectiveImportDeps: [
          {
            sheet: 'Users',
            dependsOn: 'Departments must already exist in the system. Location is scoped to your assigned location.',
          },
          {
            sheet: 'Devices',
            dependsOn: 'Device Names and Departments must already exist in the system. Location is scoped to your assigned location.',
          },
        ],
        locationNotes: `Your assigned location is: ${user?.location_name || user?.locations?.[0] || 'Not configured'}. All imports are automatically restricted to this location. If you need to add devices to a different location, contact your System Administrator.`,
        relationshipIntro: 'Users and Devices use Departments and your Location (managed by System Admin). Need a new department? Contact your System Administrator.',
        keyReminders: [
          {
            title: 'Duplicates flagged in upload',
            items: [
              'Users: username, email, contact number, employee_id',
              'Devices: serial_number and repeated device_identifier (except new-device)',
            ],
          },
          {
            title: 'Users',
            items: [
              'New rows need location_code',
              'department_name can be comma-separated (full list on update)',
              'Email change re-verifies; resolve pending links / deactivated accounts in User Management first',
            ],
          },
          {
            title: 'Devices',
            items: [
              'Device location cannot change after creation',
              'Custodian / technicians use unique employee IDs',
              'Technicians must be active and assigned to your location and the device department',
            ],
          },
        ],
        rulesNote: 'Local pre-check shows issues but you can still import. The server enforces the same rules and lists every issue per row.',
      };
    }
  };

  const roleGuideContent = getRoleSpecificGuideContent();

  const renderImportOrderSteps = (steps) => {
    if (!steps?.length) return null;
    return (
      <div className="di-import-order">
        <div className="di-import-order-label">Full onboarding order</div>
        <p className="di-import-order-steps" aria-label={`Import order: ${steps.join(', then ')}`}>
          {steps.map((step, idx) => (
            <span key={step} className="di-import-order-step-wrap">
              {idx > 0 && <span className="di-import-order-arrow" aria-hidden="true">→</span>}
              <span className="di-import-order-step">
                <span className="di-import-order-num" aria-hidden="true">{idx + 1}</span>
                {step}
              </span>
            </span>
          ))}
        </p>
      </div>
    );
  };

  const renderSelectiveImportDeps = (deps) => {
    if (!deps?.length) return null;
    return (
      <div className="di-selective-deps">
        <div className="di-selective-deps-label">Selective import — what must already exist</div>
        <p className="di-selective-deps-intro">
          You can upload a subset of sheets. References may come from the same file or from data already in the system.
        </p>
        <div className="di-table-wrap">
          <table className="di-deps-table">
            <thead>
              <tr>
                <th scope="col">Sheet</th>
                <th scope="col">Depends on</th>
              </tr>
            </thead>
            <tbody>
              {deps.map((row) => (
                <tr key={row.sheet}>
                  <td className="di-deps-sheet">{row.sheet}</td>
                  <td>{row.dependsOn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  /**
   * Get sheet structure filtered by user role
   * Superadmin: All sheets
   * Admin: Only Users and Devices
   */
  const getSheetStructure = () => {
    const allSheets = {
      'Device Names': {
        order: 1,
        description: 'Standardized medical device names and categories',
        fields: [
          { name: 'name', type: 'Text', required: true, description: 'Device name (must be unique in system)', example: 'CT Scanner' },
          { name: 'code', type: 'Text', required: true, description: 'Unique device code', example: 'CT-001' },
          { name: 'category', type: 'Text', required: false, description: 'Device category', example: 'Imaging' },
          { name: 'description', type: 'Text', required: false, description: 'Additional details', example: 'High-resolution imaging device' },
          { name: 'criticality', type: 'Text', required: true, description: 'Device criticality for complaint priority (critical, major, or minor)', example: 'critical' }
        ],
        relationships: [],
        notes: 'Import this first. Device names are referenced by Devices sheet. Names must be unique and consistent.'
      },
      'Departments': {
        order: 2,
        description: 'Hospital departments and units',
        fields: [
          { name: 'name', type: 'Text', required: true, description: 'Department name (must be unique in system)', example: 'Radiology' },
          { name: 'code', type: 'Text', required: true, description: 'Department code (unique)', example: 'RAD' },
          { name: 'icon', type: 'Text', required: false, description: 'Icon emoji or identifier', example: '🏥' },
          { name: 'description', type: 'Text', required: false, description: 'Department description', example: 'Medical imaging and diagnostic services' }
        ],
        relationships: [],
        notes: 'Import second. Department names are referenced by Users and Devices sheets. Names must be unique and consistent.'
      },
      'Locations': {
        order: 3,
        description: 'Hospital locations/branches',
        fields: [
          { name: 'name', type: 'Text', required: true, description: 'Location name (human-readable)', example: 'Apollo Hospital - Delhi' },
          { name: 'location_code', type: 'Text', required: true, description: 'Location code (must be unique in system)', example: 'MAIN-CAMPUS' },
          { name: 'contact_email', type: 'Email', required: true, description: 'Contact email', example: 'delhi@apollohospital.com' },
          { name: 'contact_phone', type: 'Phone', required: true, description: 'Contact phone — 10-digit Indian mobile only; do not include country code (+91)', example: '9876543210' },
          { name: 'address', type: 'Text', required: true, description: 'Street address', example: '21, Prag Raj Samaj, Saket' },
          { name: 'city', type: 'Text', required: true, description: 'City', example: 'Delhi' },
          { name: 'state', type: 'Text', required: true, description: 'State/Province', example: 'Delhi' },
          { name: 'country', type: 'Text', required: true, description: 'Country', example: 'India' }
        ],
        relationships: [],
        notes: 'Import third. Location codes are unique per organization. max_users (default: 50) and max_devices (default: 1000) are auto-set if not provided. Location names are referenced by Users and Devices sheets.'
      },
      'Complaint Categories': {
        order: 4,
        description: 'Org-scoped complaint category catalog with optional location/device-name applicability',
        fields: [
          { name: 'code', type: 'Text', required: true, description: 'Unique category code per organization (upsert key)', example: 'device_not_working' },
          { name: 'label', type: 'Text', required: true, description: 'Display label shown on complaint forms', example: 'Device Not Working' },
          { name: 'sort_order', type: 'Number', required: false, description: 'Display order (integer; default 0)', example: '10' },
          { name: 'is_active', type: 'Yes/No', required: false, description: 'Active categories appear on new complaints; default TRUE. System Other cannot be deactivated.', example: 'TRUE' },
          { name: 'applies_to_all_locations', type: 'Yes/No', required: false, description: 'TRUE = all locations; FALSE requires location_codes. Default TRUE.', example: 'TRUE' },
          { name: 'location_codes', type: 'Text', required: false, description: 'Comma-separated location_codes when applies_to_all_locations is FALSE', example: 'MAIN-CAMPUS, NORTH-WING' },
          { name: 'applies_to_all_device_names', type: 'Yes/No', required: false, description: 'TRUE = all device names; FALSE requires device_names. Default TRUE.', example: 'TRUE' },
          { name: 'device_names', type: 'Text', required: false, description: 'Comma-separated device names when applies_to_all_device_names is FALSE', example: 'CT Scanner, MRI' }
        ],
        relationships: [
          'References Locations sheet via location_codes (comma-separated; matches by exact location_code, case-insensitive)',
          'References Device Names sheet via device_names (comma-separated; matches by exact name, case-insensitive)',
          'Location/Device Name MUST exist either in the upload OR in the system when applicability is narrowed',
          'System category (typically code "other") always applies to all locations and device names'
        ],
        notes: 'Import fourth (after Device Names and Locations). Upserts by code. System Other is excluded from export (managed by the system, like the system location). If present on import, only label/sort_order updates are allowed; deactivate or narrowing applicability is rejected.'
      },
      'Users': {
        order: 5,
        description: 'System users with roles and assignments',
        fields: [
          { name: 'username', type: 'Text', required: true, description: 'Unique username', example: 'ravi.kumar' },
          ...(employeeIdMode === 'manual' ? [
            { name: 'employee_id', type: 'Text', required: true, description: 'Employee ID from HRIS/external system', example: 'EMP12345' }
          ] : []),
          { name: 'email', type: 'Email', required: true, description: 'User email (globally unique). On update, a changed email triggers re-verification like User Management.', example: 'ravi.kumar@apollohospital.com' },
          { name: 'full_name', type: 'Text', required: true, description: 'Full name', example: 'Ravi Kumar Sharma' },
          { name: 'contact_number', type: 'Phone', required: true, description: 'Contact number — 10-digit Indian mobile only; do not include country code (+91)', example: '9876543210' },
          { name: 'role', type: 'Text', required: true, description: 'User role (superadmin, admin, manager, technician, coordinator, custodian, central_help_desk)', example: 'technician' },
          { name: 'department_name', type: 'Text', required: true, description: 'One or more department names, comma-separated (e.g. Radiology, REHAB). REQUIRED for technician, coordinator, custodian. MUST BE EMPTY for location-scoped roles (admin, manager, central_help_desk). On update, replaces the full department assignment list.', example: 'Radiology, REHAB' },
          { name: 'location_code', type: 'Text', required: true, description: 'Location code (must exist in Locations sheet or in the system)', example: 'MAIN-CAMPUS' }
        ],
        relationships: [
          'References Departments sheet via department_name (comma-separated names; matches by exact name, case-insensitive)',
          'References Locations sheet via location_code (matches by exact code)',
          'Department/Location MUST exist either in the upload OR in the system',
          'System will return error if referenced entity does not exist',
          'Location-scoped roles (admin, central_help_desk, manager) should NOT have department assigned'
        ],
        notes: user?.role === 'superadmin' ? 'Import fifth. Department assignment depends on user role: location-scoped roles (admin, central_help_desk, manager) must have empty department_name; other roles need one or more valid department names (comma-separated). Re-importing a user updates the full department list from the cell.' : 'Import first (step 1). Non-location-scoped roles require at least one department (comma-separated for multiple). Location is auto-assigned to your location and cannot be changed.'
      },
      'Devices': {
        order: 6,
        description: 'Medical devices with specifications and maintenance info',
        fields: [
          { name: 'device_identifier', type: 'Text', required: true, description: 'System-generated unique ID. Use <NEW> for new devices (auto-generated as EQ-1001, EQ-1002, etc.). For existing devices, provide actual device_identifier (e.g., EQ-1001) to update', example: '<NEW>' },
          { name: 'device_name', type: 'Text', required: true, description: 'Device name (must exist in Device Names sheet or in the system)', example: 'CT Scanner' },
          { name: 'model', type: 'Text', required: true, description: 'Device model', example: 'Siemens SOMATOM Drive' },
          { name: 'serial_number', type: 'Text', required: true, description: 'Serial number (unique per organization - duplicates trigger update, not insert)', example: 'SN-2023-0045' },
          { name: 'year_of_manufacture', type: 'Number', required: true, description: 'Year of manufacture', example: '2023' },
          { name: 'manufacturer', type: 'Text', required: true, description: 'Manufacturer name', example: 'Siemens Healthcare' },
          { name: 'supplier', type: 'Text', required: false, description: 'Supplier name', example: 'Medtech Solutions India Pvt Ltd' },
          { name: 'installation_date', type: 'Date', required: true, description: 'Installation date (YYYY-MM-DD). Import accepts historical dates for onboarding; must be on or after year_of_manufacture and purchase_date if provided', example: '2023-06-15' },
          { name: 'product_cost', type: 'Number', required: true, description: 'Product cost (in INR)', example: '4500000' },
          { name: 'custodian_employee_id', type: 'Text', required: true, description: 'Unique employee ID of the active user designated as device custodian.', example: 'EMP00123' },
          { name: 'department_name', type: 'Text', required: true, description: 'Department name (must exist in Departments sheet or in the system)', example: 'Radiology' },
          { name: 'location_code', type: 'Text', required: true, description: 'Location code (must exist in Locations sheet or in the system). IMMUTABLE - cannot be changed after device creation', example: 'MAIN-CAMPUS' },
          { name: 'physical_location', type: 'Text', required: true, description: 'Physical location description', example: 'Building C, Ground Floor, East Wing' },
          { name: 'warranty_period', type: 'Number', required: false, description: 'Warranty period in months', example: '24' },
          { name: 'warranty_expiry', type: 'Date', required: false, description: 'Warranty expiry date', example: '2025-06-15' },
          { name: 'service_contract_type', type: 'Text', required: true, description: 'Service contract type (must be one of: LAMC, CAMC, Warranty, In-house)', example: 'LAMC' },
          { name: 'ppm_frequency', type: 'Text', required: false, description: 'PPM frequency', example: 'Bi-annually' },
          { name: 'calibration_required', type: 'Yes/No', required: false, description: 'Calibration required', example: 'Yes' },
          { name: 'compliance_required', type: 'Yes/No', required: false, description: 'Compliance required', example: 'Yes' },
          { name: 'primary_technician_employee_id', type: 'Text', required: false, description: 'Optional unique employee ID of the primary technician. Must be active and assigned to the device department and location.', example: 'EMP00456' },
          { name: 'secondary_technician_employee_id', type: 'Text', required: false, description: 'Optional unique employee ID of the secondary technician. If both are supplied, it must differ from primary_technician_employee_id.', example: 'EMP00789' }
        ],
        relationships: [
          'device_identifier: Use <NEW> for new devices, or provide actual ID (e.g., EQ-1001) to update existing device',
          'References Device Names sheet via device_name (matches by exact name)',
          'References Departments sheet via department_name (matches by exact name)',
          'References Locations sheet via location_code (matches by exact code)',
          'custodian_employee_id uniquely identifies the active user designated as device custodian',
          'primary_technician_employee_id and secondary_technician_employee_id identify active technicians in the device department and location',
          'When technician columns are included for an existing device, blank values clear its technician assignments',
          'Device Name/Department/Location MUST exist either in the upload OR in the system',
          'System will return error if referenced entity does not exist',
          'Serial number duplicates: If serial_number already exists, provide device_identifier to update that device',
          'installation_date: To correct a date beyond ±30 days from the current value, re-import the device row with its device_identifier (historical dates allowed on import)'
        ],
        notes: user?.role === 'superadmin' ? 'Import last. Depends on Device Names, Departments, and Locations sheets. Use <NEW> keyword for new devices; location_code is immutable after creation. Serial number must be unique within organization. Historical installation_date values are allowed for onboarding legacy devices.' : 'Import second (step 2). Device Names and Departments must exist in the system (created by your System Administrator). location_code is auto-assigned to your location and cannot be changed. Serial number must be unique within organization. Historical installation_date values are allowed; use import to fix dates outside the UI ±30-day correction window.'
      }
    };

    // Filter sheets based on user role
    if (user?.role === 'superadmin') {
      return allSheets;
    } else if (user?.role === 'admin') {
      // Admin can only see Users and Devices
      // Reassign order numbers to 1 and 2 for admin context
      const adminSheets = {
        'Users': { ...allSheets['Users'], order: 1 },
        'Devices': { ...allSheets['Devices'], order: 2 }
      };
      return adminSheets;
    }
    return {};
  };

  /**
   * Detailed sheet structure with field metadata, types, descriptions, and relationships
   */
  const SHEET_STRUCTURE = getSheetStructure();
  const guideSheetNames = Object.keys(SHEET_STRUCTURE);

  const openReferenceModal = () => {
    setGuideTab('overview');
    setShowReferenceModal(true);
  };

  const renderGuideSheetFields = (sheetName) => {
    const sheetData = SHEET_STRUCTURE[sheetName];
    if (!sheetData) return null;
    return (
      <div className="di-sheet-block di-sheet-block--tab">
        <div className="di-sheet-header">
          <span className="di-sheet-order" aria-hidden="true">{sheetData.order}</span>
          <div>
            <h4>{sheetName}</h4>
            <p>{sheetData.description}</p>
          </div>
        </div>

        <div className="di-table-wrap">
          <table className="di-field-table">
            <thead>
              <tr>
                <th>Field name</th>
                <th className="di-col-type">Type</th>
                <th className="di-col-req">Required</th>
                <th>Description</th>
                <th className="di-col-example">Example</th>
              </tr>
            </thead>
            <tbody>
              {sheetData.fields.map((field, idx) => {
                const typeMod =
                  field.type === 'Date' ? 'di-type-chip--date'
                    : field.type === 'Number' ? 'di-type-chip--number'
                      : field.type === 'Email' ? 'di-type-chip--email'
                        : field.type === 'Phone' ? 'di-type-chip--phone'
                          : field.type === 'Yes/No' ? 'di-type-chip--yesno'
                            : '';
                return (
                  <tr key={idx}>
                    <td className="di-field-name">{field.name}</td>
                    <td>
                      <span className={`di-type-chip ${typeMod}`.trim()}>{field.type}</span>
                    </td>
                    <td className={field.required ? 'di-req-yes' : 'di-req-no'}>
                      {field.required ? '✓' : '○'}
                    </td>
                    <td>{field.description}</td>
                    <td className="di-field-example">{field.example}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sheetData.relationships.length > 0 && (
          <div className="di-callout di-callout--warning">
            <h5>Field relationships</h5>
            <ul>
              {sheetData.relationships.map((rel, idx) => (
                <li key={idx}>{rel}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="di-callout di-callout--info">
          <h5>Note</h5>
          <p>{sheetData.notes}</p>
        </div>
      </div>
    );
  };

  /**
   * Process file - Parse locally and show preview
   */
  const processFile = async (file) => {
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];

    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    // Parse file locally
    await parseExcelFile(file);
  };

  /**
   * Open import modal for file selection / review
   */
  const openImportModal = () => {
    setShowImportModal(true);
    setImportModalState('file-selection');
    setPreviewData(null);
    setImportResults(null);
    setImportProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileBrowser = (e) => {
    e?.stopPropagation?.();
    fileInputRef.current?.click();
  };

  /**
   * Location-scoped roles (not tied to a department)
   */
  const LOCATION_SCOPED_ROLES = ['admin', 'central_help_desk', 'manager'];

  /**
   * Helper function to check if a value is empty
   */
  const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    return false;
  };

  /**
   * Helper function to check if an entire row is empty
   * A row is considered empty if all its values are empty (null, undefined, or blank strings)
   */
  const isRowEmpty = (row) => {
    if (!row || typeof row !== 'object') return true;
    return Object.entries(row).every(([key, value]) => {
      if (key === '__excelRowNumber') return true;
      return isEmptyValue(value);
    });
  };

  const normalizeRowKeys = (row) => {
    const out = {};
    if (!row || typeof row !== 'object') return out;
    Object.keys(row).forEach((k) => {
      const nk = String(k || '').trim().toLowerCase();
      if (!nk) return;
      out[nk] = row[k];
    });
    return out;
  };

  const mergeRowIssueMaps = (base, add) => {
    const out = { ...base };
    Object.keys(add || {}).forEach((sheet) => {
      if (!out[sheet]) out[sheet] = {};
      Object.keys(add[sheet]).forEach((row) => {
        if (!out[sheet][row]) out[sheet][row] = [];
        out[sheet][row] = out[sheet][row].concat(add[sheet][row]);
      });
    });
    return out;
  };

  const pushRowIssue = (rowMap, sheetName, excelRowNumber, message) => {
    if (!rowMap[sheetName]) rowMap[sheetName] = {};
    if (!rowMap[sheetName][excelRowNumber]) rowMap[sheetName][excelRowNumber] = [];
    rowMap[sheetName][excelRowNumber].push(message);
  };

  /**
   * Per-row required / role rules. Returns { rowIssuesBySheet, flatList }.
   * Does not block import by itself (combined with template checks in parent).
   */
  const validateMandatoryFieldsRowIssues = (sheetsData) => {
    const rowIssuesBySheet = {};
    const flatList = [];

    const add = (sheetName, rowNum, msg) => {
      pushRowIssue(rowIssuesBySheet, sheetName, rowNum, msg);
      flatList.push(`⛔ ${sheetName} Row ${rowNum}: ${msg}`);
    };

    try {
      Object.entries(sheetsData).forEach(([sheetName, rows]) => {
        if (!SHEET_STRUCTURE[sheetName] || rows.length === 0) return;

        const requiredFields = SHEET_STRUCTURE[sheetName].fields
          .filter(field => field.required)
          .map(field => field.name.toLowerCase());

        rows.forEach((row) => {
          const excelRowNumber = row.__excelRowNumber || 'unknown';

          requiredFields.forEach((fieldName) => {
            if (sheetName === 'Users' && fieldName === 'department_name') return;

            const cellValue = row[fieldName];
            if (isEmptyValue(cellValue)) {
              add(sheetName, excelRowNumber, `Required field "${fieldName}" is empty`);
            }
          });

          if (sheetName === 'Users') {
            const role = row['role']?.toString().toLowerCase().trim();
            const departmentName = row['department_name']?.toString().trim();

            if (role && LOCATION_SCOPED_ROLES.includes(role)) {
              if (departmentName) {
                add('Users', excelRowNumber, `Role "${role}" is location-scoped; leave department_name empty.`);
              }
            } else if (role && !LOCATION_SCOPED_ROLES.includes(role)) {
              if (isEmptyValue(departmentName)) {
                add('Users', excelRowNumber, `Role "${role}" requires department_name.`);
              }
            }
          }
        });
      });

      return { rowIssuesBySheet, flatList };
    } catch (error) {
      console.error('Error validating mandatory fields:', error);
      return { rowIssuesBySheet: {}, flatList: [] };
    }
  };

  const classifyRowIssueType = (msg) => {
    const m = String(msg || '').toLowerCase();
    if (m.includes('required field') || m.includes(' is empty')) return 'Required';
    if (m.includes('duplicate')) return 'Duplicate';
    if (m.includes('location-scoped') || m.includes('department_name')) return 'Role-rule';
    return 'Other';
  };

  const computeRowIssueTypeBreakdown = (rowIssuesBySheet) => {
    const perSheet = {};
    const total = { Required: 0, Duplicate: 0, 'Role-rule': 0, Other: 0 };

    Object.keys(rowIssuesBySheet || {}).forEach((sheet) => {
      const perRow = rowIssuesBySheet[sheet] || {};
      const sheetCounts = { Required: 0, Duplicate: 0, 'Role-rule': 0, Other: 0 };

      Object.keys(perRow).forEach((rn) => {
        (perRow[rn] || []).forEach((msg) => {
          const t = classifyRowIssueType(msg);
          sheetCounts[t] = (sheetCounts[t] || 0) + 1;
          total[t] = (total[t] || 0) + 1;
        });
      });

      perSheet[sheet] = sheetCounts;
    });

    return { total, perSheet };
  };

  const computePreValidationSummary = (filteredSheetsData, rowIssuesBySheet) => {
    const perSheet = {};
    const totals = { total: 0, passed: 0, failed: 0 };

    Object.keys(filteredSheetsData || {}).forEach((sheet) => {
      const rows = filteredSheetsData[sheet] || [];
      const totalRows = rows.length;

      const issueRows = rowIssuesBySheet?.[sheet] ? new Set(Object.keys(rowIssuesBySheet[sheet])) : new Set();
      const failed = issueRows.size;
      const passed = Math.max(0, totalRows - failed);

      perSheet[sheet] = { total: totalRows, passed, failed };
      totals.total += totalRows;
      totals.passed += passed;
      totals.failed += failed;
    });

    return { totals, perSheet };
  };

  const computeDistinctIssueSummary = (rowIssuesBySheet, cap = 8) => {
    const counts = new Map();
    Object.keys(rowIssuesBySheet || {}).forEach((sheet) => {
      const perRow = rowIssuesBySheet[sheet] || {};
      Object.keys(perRow).forEach((rn) => {
        (perRow[rn] || []).forEach((msg) => {
          const key = String(msg || '').trim();
          if (!key) return;
          counts.set(key, (counts.get(key) || 0) + 1);
        });
      });
    });

    const items = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([message, count]) => ({ message, count }));

    return {
      totalDistinct: items.length,
      top: items.slice(0, cap),
      remainder: Math.max(0, items.length - cap),
    };
  };

  const collectIntraUploadDuplicateRowIssues = (sheetsData) => {
    const rowIssues = {};

    const collectKey = (sheet, rows, key, label, normalizer) => {
      const map = {};
      rows.forEach((row) => {
        const raw = row[key];
        if (isEmptyValue(raw)) return;
        const n = normalizer(String(raw));
        if (!n) return;
        if (!map[n]) map[n] = [];
        map[n].push(row.__excelRowNumber);
      });
      Object.keys(map).forEach((k) => {
        const group = map[k];
        if (group.length < 2) return;
        group.forEach((r) => {
          const others = group.filter((x) => x !== r);
          const msg = `Duplicate ${label} in this file (also row(s) ${others.join(', ')})`;
          pushRowIssue(rowIssues, sheet, r, msg);
        });
      });
    };

    if (sheetsData['Device Names']?.length) {
      collectKey('Device Names', sheetsData['Device Names'], 'name', 'name', (s) => s.trim().toLowerCase());
      collectKey('Device Names', sheetsData['Device Names'], 'code', 'code', (s) => s.trim().toLowerCase());
    }
    if (sheetsData.Departments?.length) {
      collectKey('Departments', sheetsData.Departments, 'name', 'name', (s) => s.trim().toLowerCase());
      collectKey('Departments', sheetsData.Departments, 'code', 'code', (s) => s.trim().toLowerCase());
    }
    if (sheetsData.Locations?.length) {
      collectKey('Locations', sheetsData.Locations, 'location_code', 'location_code', (s) => s.trim().toLowerCase());
    }
    if (sheetsData['Complaint Categories']?.length) {
      collectKey('Complaint Categories', sheetsData['Complaint Categories'], 'code', 'code', (s) => s.trim().toLowerCase());
    }

    if (sheetsData.Users?.length) {
      const urows = sheetsData.Users;
      collectKey('Users', urows, 'username', 'username', (s) => s.trim().toLowerCase());
      collectKey('Users', urows, 'email', 'email', (s) => s.trim().toLowerCase());
      collectKey('Users', urows, 'contact_number', 'contact_number', (s) => s.trim().toLowerCase());
      if (employeeIdMode === 'manual') {
        collectKey('Users', urows, 'employee_id', 'employee_id', (s) => s.trim().toLowerCase());
      }
    }

    if (sheetsData.Devices?.length) {
      const drows = sheetsData.Devices;
      collectKey('Devices', drows, 'serial_number', 'serial_number', (s) => s.trim().toLowerCase());
      const byIdent = {};
      drows.forEach((row) => {
        const raw = row['device_identifier'];
        if (isEmptyValue(raw)) return;
        const t = String(raw).trim();
        if (t.toUpperCase() === '<NEW>') return;
        const n = t.toLowerCase();
        if (!byIdent[n]) byIdent[n] = [];
        byIdent[n].push(row.__excelRowNumber);
      });
      Object.keys(byIdent).forEach((k) => {
        const group = byIdent[k];
        if (group.length < 2) return;
        group.forEach((r) => {
          const others = group.filter((x) => x !== r);
          const msg = `Duplicate device_identifier in this file (also row(s) ${others.join(', ')})`;
          pushRowIssue(rowIssues, 'Devices', r, msg);
        });
      });
    }

    return rowIssues;
  };

  const rowIssuesToFlatList = (rowMap) => {
    const flat = [];
    Object.keys(rowMap).forEach((sheet) => {
      Object.keys(rowMap[sheet]).forEach((rn) => {
        rowMap[sheet][rn].forEach((msg) => {
          flat.push(`⛔ ${sheet} Row ${rn}: ${msg}`);
        });
      });
    });
    return flat;
  };

  /**
   * Parse Excel file locally and validate template structure
   * Allows partial imports - only requires at least one sheet with data
   */
  const parseExcelFile = async (file) => {
    try {
      setIsLoading(true);

      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { header: 1 });

      const sheetNames = workbook.SheetNames;
      const sheetsInfo = [];
      let validationErrors = [];
      let validationWarnings = [];
      let missingSheetsWarnings = [];
      let ignoredSheetsWarnings = [];
      const sheetsData = {};
      let sheetsWithData = 0;

      // If an admin uploads a superadmin template, warn that restricted sheets will be ignored.
      // (Backend will also ignore/deny restricted sheets; this is a UX hint.)
      if (user?.role === 'admin') {
        const restrictedSheets = ['Device Names', 'Departments', 'Locations', 'Complaint Categories'];
        restrictedSheets.forEach((sName) => {
          if (!sheetNames.includes(sName)) return;
          try {
            const s = workbook.Sheets[sName];
            const aoa = XLSX.utils.sheet_to_json(s, { header: 1, defval: null, blankrows: true });
            const hasAnyDataRow = (aoa.slice(1) || []).some((row) => Array.isArray(row) && row.some((v) => v !== null && v !== ''));
            if (hasAnyDataRow) {
              ignoredSheetsWarnings.push(`⚠️ Sheet "${sName}" exists and has data, but will be ignored for your role (you can only import Users and Devices).`);
            } else {
              ignoredSheetsWarnings.push(`ℹ️ Sheet "${sName}" exists, but will be ignored for your role (you can only import Users and Devices).`);
            }
          } catch (e) {
            ignoredSheetsWarnings.push(`ℹ️ Sheet "${sName}" exists, but will be ignored for your role (you can only import Users and Devices).`);
          }
        });
      }

      // Missing expected sheets: persona-aware, non-blocking notices
      Object.keys(EXPECTED_SHEETS).forEach((expName) => {
        if (sheetNames.includes(expName)) return;
        if (user?.role === 'admin' && !['Users', 'Devices'].includes(expName)) {
          return;
        }
        missingSheetsWarnings.push(`ℹ️ Sheet "${expName}" was not found in the uploaded file (it will be skipped).`);
      });

      // Validate each sheet structure
      sheetNames.forEach(sheetName => {
        if (EXPECTED_SHEETS[sheetName]) {
          const sheet = workbook.Sheets[sheetName];
          // Always get headers from the sheet, even if no data rows.
          // Use an array-of-arrays so we can preserve true Excel row numbers
          // even when there are blank rows in between.
          const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null, blankrows: true });
          const headers = aoa[0] || [];
          const data = (aoa.slice(1) || []).map((rowArr) => {
            const obj = {};
            headers.forEach((h, idx) => {
              if (!h) return;
              obj[String(h).trim()] = rowArr ? rowArr[idx] : null;
            });
            return obj;
          });
          const expectedColumns = EXPECTED_SHEETS[sheetName];

          let status = 'empty';

          // VALIDATE COLUMN STRUCTURE FOR ANY SHEET THAT EXISTS
          // This prevents users from uploading templates with wrong columns but no data
          const missingColumns = expectedColumns.filter(col =>
            !headers.some(h => h && h.toString().toLowerCase() === col.toLowerCase())
          );

          if (missingColumns.length > 0) {
            // Template structure is invalid - mark as error regardless of data
            validationErrors.push(`Sheet "${sheetName}" missing columns: ${missingColumns.join(', ')}`);
            status = 'invalid';
          } else if (data.length === 0) {
            // Columns are correct but no data rows
            validationWarnings.push(`⚠️ Sheet "${sheetName}" has header but no data rows (will be skipped)`);
            status = 'empty';
          } else {
            // Filter out empty rows and count only rows with actual data
            const nonEmptyRows = data.filter(row => !isRowEmpty(row));

            if (nonEmptyRows.length === 0) {
              // All rows are empty
              validationWarnings.push(`⚠️ Sheet "${sheetName}" has header but all data rows are empty (will be skipped)`);
              status = 'empty';
            } else {
              // Has correct columns and non-empty data
              sheetsWithData++;
              status = 'has-data';
            }
          }

          // Calculate non-empty row count for display
          const displayRowCount = status === 'has-data'
            ? data.filter(row => !isRowEmpty(row)).length
            : data.length;

          sheetsInfo.push({
            name: sheetName,
            rowCount: displayRowCount,
            columns: headers.filter(h => h), // Filter out empty headers
            status: status
          });

          // Store data for reference name validation
          sheetsData[sheetName] = data;
        }
      });

      // Row-level validation: required fields, role rules, within-file duplicates (non-blocking for Proceed)
      let rowIssuesBySheet = {};
      let rowLevelSampleMessages = [];
      let totalRowIssueCount = 0;
      let duplicateSummaryWarnings = [];
      let preValidationSummary = null;
      let distinctIssueSummary = null;

      if (sheetsWithData > 0 && validationErrors.length === 0) {
        const filteredSheetsData = {};
        Object.entries(sheetsData).forEach(([sheetName, rows]) => {
          filteredSheetsData[sheetName] = rows
            .map((row, index) => ({ row, excelRowNumber: index + 2 }))
            // Skip completely empty rows, but preserve true Excel row numbers for everything else
            .filter(({ row }) => !isRowEmpty(row))
            .map(({ row, excelRowNumber }) => ({
              ...normalizeRowKeys(row),
              __excelRowNumber: excelRowNumber,
            }));
        });

        const m = validateMandatoryFieldsRowIssues(filteredSheetsData);
        const d = collectIntraUploadDuplicateRowIssues(filteredSheetsData);
        rowIssuesBySheet = mergeRowIssueMaps(m.rowIssuesBySheet, d);
        rowLevelSampleMessages = rowIssuesToFlatList(rowIssuesBySheet);
        totalRowIssueCount = rowLevelSampleMessages.length;
        preValidationSummary = computePreValidationSummary(filteredSheetsData, rowIssuesBySheet);
        distinctIssueSummary = computeDistinctIssueSummary(rowIssuesBySheet, 8);

        let dupCount = 0;
        Object.keys(d).forEach((s) => {
          Object.keys(d[s] || {}).forEach((rn) => {
            dupCount += (d[s][rn] || []).length;
          });
        });
        if (dupCount > 0) {
          duplicateSummaryWarnings.push(
            `Within-file duplicate value(s) detected: ${dupCount} issue(s) (see row list below; backend enforces the same).`
          );
        }
      }

      setIsLoading(false);

      // Block import for template/structure or no data.
      // Additionally block when *every* non-empty record has local issues (no rows would pass pre-check).
      const hasBlockingErrors = validationErrors.length > 0 || sheetsWithData === 0;
      const allRowsFailedPreValidation =
        preValidationSummary?.totals?.total > 0 &&
        preValidationSummary?.totals?.passed === 0;
      const isValid = !hasBlockingErrors && !allRowsFailedPreValidation;
      const hasRowLevelFindings = totalRowIssueCount > 0;
      const rowIssueTypeBreakdown = hasRowLevelFindings ? computeRowIssueTypeBreakdown(rowIssuesBySheet) : null;

      // Show preview with validation results
      setPreviewData({
        fileName: file.name,
        fileSize: (file.size / 1024).toFixed(2),
        lastModified: new Date(file.lastModified).toLocaleString(),
        sheets: sheetsInfo,
        isValid,
        allRowsFailedPreValidation,
        hasRowLevelFindings,
        totalRowIssueCount,
        rowIssueTypeBreakdown,
        preValidationSummary,
        distinctIssueSummary,
        validationErrors,
        validationWarnings,
        missingSheetsWarnings,
        ignoredSheetsWarnings,
        duplicateWarnings: duplicateSummaryWarnings,
        rowIssuesBySheet,
        mandatoryFieldErrors: rowLevelSampleMessages,
        sheetsWithData,
        file: file
      });
      return true;
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      setIsLoading(false);
      toast.error('Failed to parse Excel file. Please ensure it\'s a valid Excel file.');
      return false;
    }
  };

  const handleDownloadPreValidation = () => {
    if (!previewData?.rowIssuesBySheet) return;
    const lines = [['Sheet', 'Excel row', 'Issue']];
    const sheetOrder = ['Device Names', 'Departments', 'Locations', 'Complaint Categories', 'Users', 'Devices'];
    const sheets = Object.keys(previewData.rowIssuesBySheet);
    sheets
      .sort((a, b) => {
        const ia = sheetOrder.indexOf(a);
        const ib = sheetOrder.indexOf(b);
        const ra = ia === -1 ? 999 : ia;
        const rb = ib === -1 ? 999 : ib;
        if (ra !== rb) return ra - rb;
        return a.localeCompare(b);
      })
      .forEach((sheet) => {
        const perRow = previewData.rowIssuesBySheet[sheet] || {};
        Object.keys(perRow)
          .sort((a, b) => Number(a) - Number(b))
          .forEach((rn) => {
            (perRow[rn] || []).forEach((msg) => lines.push([sheet, String(rn), msg]));
          });
      });
    if (lines.length < 2) {
      toast.info('No row-level issues to export.');
      return;
    }
    const ws = XLSX.utils.aoa_to_sheet(lines);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'row_issues');
    const base = (previewData.fileName || 'import').replace(/\.[^/.]+$/, '');
    XLSX.writeFile(wb, `pre_validation_${base}.xlsx`);
    toast.success('Pre-validation report downloaded.');
  };

  /**
   * Close import modal and reset state
   */
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportModalState('file-selection');
    setPreviewData(null);
    setImportResults(null);
    setImportProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Confirm and start import after preview validation
   */
  const confirmImport = async () => {
    if (previewData?.file && previewData?.isValid) {
      const file = previewData.file;
      setImportModalState('progress');
      await uploadFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('is-dragover');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('is-dragover');
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('is-dragover');

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await processFile(files[0]);
    }
  };

  /**
   * Upload and process the Excel file
   * Keeps preview modal open and shows progress inside it
   */
  const uploadFile = async (file) => {
    try {
      setIsLoading(true);
      setImportProgress({ status: 'uploading', message: 'Uploading file...', progress: 0 });

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        API_ENDPOINTS.DATA_IMPORT.IMPORT_DATA,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setImportProgress({
              status: 'uploading',
              message: `Uploading file... ${percentCompleted}%`,
              progress: percentCompleted
            });
          }
        }
      );

      // Update progress to processing (keep progress at 100% during processing)
      setImportProgress({ status: 'processing', message: 'Processing data...', progress: 100 });

      // Simulate processing time for better UX
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Transition to results state
      setImportResults(response.data);
      setImportProgress(null);
      setImportModalState('results');
    } catch (error) {
      console.error('Error uploading file:', error);
      setImportProgress(null);

      // Show detailed errors if available
      if (error.response?.data) {
        const errorData = error.response.data;
        let errorsList = [];

        // Handle validation errors (missing columns, access denied, no data, etc.)
        if (errorData.error) {
          let errorMessage = errorData.error;

          // If missing columns are provided, append them to the main error message
          if (errorData.missing_columns && Array.isArray(errorData.missing_columns)) {
            errorMessage += `\nMissing columns: ${errorData.missing_columns.join(', ')}`;
          }

          errorsList.push({
            row: 'Template Validation',
            message: errorMessage
          });
        }
        // Handle errors from successful import with failures (details array)
        else if (errorData.details && Array.isArray(errorData.details)) {
          errorsList = errorData.details;
        }

        if (errorsList.length > 0) {
          setImportResults({
            summary: { created: 0, updated: 0, errors: errorsList.length },
            errors: errorsList
          });
          setImportModalState('results');
        } else {
          // Fallback: show error in toast
          toast.error(errorData.error || 'Failed to import file');
        }
      } else {
        // No response data, just show a generic error
        toast.error('Failed to import file');
      }
    } finally {
      setIsLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Download Excel file with authentication token
   */
  const handleDownloadResults = async (downloadUrl) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        downloadUrl,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from URL or use default
      const urlParts = downloadUrl.split('/');
      const filename = urlParts[urlParts.length - 1] || 'import_results.xlsx';

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('File downloaded successfully!');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Export current data to Excel (all available sheets, or a selected subset)
   * File is prefixed with organization name from organizations table
   */
  const handleExportData = async () => {
    if (!selectedExportSheets.length) {
      toast.error('Select at least one sheet to export.');
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.get(
        API_ENDPOINTS.DATA_IMPORT.EXPORT_DATA,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob',
          params: {
            sheets: selectedExportSheets.join(','),
          },
        }
      );

      // Create blob and download with organization name prefix
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const orgName = resolveOrganizationNameForDownload(user);
      const dateStr = new Date().toISOString().split('T')[0];
      const allSelected = selectedExportSheets.length === availableExportSheets.length;
      const fileSuffix = allSelected ? 'master_data_export' : 'selected_data_export';
      link.setAttribute('download', `${orgName}_${fileSuffix}_${dateStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(
        allSelected
          ? 'All data exported successfully!'
          : `Exported ${selectedExportSheets.length} sheet${selectedExportSheets.length === 1 ? '' : 's'} successfully!`
      );
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExportSheet = (sheetName) => {
    setSelectedExportSheets((prev) => (
      prev.includes(sheetName)
        ? prev.filter((name) => name !== sheetName)
        : [...prev, sheetName]
    ));
  };

  const selectAllExportSheets = () => {
    setSelectedExportSheets([...availableExportSheets]);
  };

  const clearExportSheets = () => {
    setSelectedExportSheets([]);
  };

  const allExportSheetsSelected =
    availableExportSheets.length > 0
    && selectedExportSheets.length === availableExportSheets.length;

  return (
    <div className="data-export-container">
      <div className="data-import-description-section">
        <h1>
          {user?.role === 'superadmin'
            ? '📥 Master Data Management'
            : '📥 Location Data Management'}
        </h1>
        <p>
          {user?.role === 'superadmin'
            ? 'Bulk import and export master data — device names, departments, locations, complaint categories, users, and devices'
            : 'Bulk import and export users and devices for your assigned location'}
        </p>
      </div>

      <div className="di-tabs" role="tablist" aria-label="Data import sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'import'}
          className={`di-tab-btn ${activeTab === 'import' ? 'active' : ''}`}
          onClick={() => setActiveTab('import')}
        >
          Import data
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'export'}
          className={`di-tab-btn ${activeTab === 'export' ? 'active' : ''}`}
          onClick={() => setActiveTab('export')}
        >
          Export data
        </button>
      </div>

      {activeTab === 'import' && (
        <div className="tab-content">
          <div className="di-step-row template-section">
            <div className="di-step-copy template-info">
              <div>
                <h4>Step 1: Download Template</h4>
                <p>Download the Excel template so columns and sheet names match what the importer expects.</p>
              </div>
            </div>
            <button
              className="action-btn action-primary"
              onClick={handleDownloadTemplate}
              disabled={isLoading}
              type="button"
            >
              <span className="action-icon">⬇️</span>
              <span className="action-text">Download Template</span>
            </button>
          </div>

          <div className="di-step-row upload-section">
            <div className="di-step-copy upload-info">
              <div>
                <h4>Step 2: Import Your File</h4>
                <p>Fill in the template, then upload the completed Excel file to validate and import.</p>
              </div>
            </div>
            <button
              className="action-btn action-primary"
              onClick={openImportModal}
              disabled={isLoading}
              type="button"
            >
              <span className="action-icon">📋</span>
              <span className="action-text">Import File</span>
            </button>
          </div>

          <div className="di-guide-section">
            <h3>Import Order Guide</h3>
            <p className="card-description">
              {roleGuideContent?.relationshipIntro || 'Follow sheet order so references resolve correctly.'}
            </p>
            <div className="di-callout di-callout--tip">
              <div className="di-callout-body">
                <h5>Before you import</h5>
                <p>
                  {roleGuideContent?.importOrderTips
                    || 'Import sheets in dependency order. Open the detailed reference for field rules, restrictions, and examples.'}
                </p>
                <div className="di-import-order-row">
                  {renderImportOrderSteps(roleGuideContent?.importOrderSteps)}
                  <button
                    className="action-btn action-secondary sheet-ref-order-tip-btn"
                    onClick={openReferenceModal}
                    type="button"
                  >
                    <span className="action-icon">📖</span>
                    <span className="action-text">View Detailed Reference</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'export' && (
        <div className="tab-content">
          <div className="di-step-row export-section">
            <div className="di-step-copy export-info">
              <div>
                <h4>Download Current Data</h4>
                <p>
                  {user?.role === 'superadmin'
                    ? 'Export master data to Excel. Choose all sheets or a subset — file format matches the import template.'
                    : 'Export users and devices for your assigned location. Choose one or both sheets.'}
                </p>
              </div>
            </div>
            <button
              className="action-btn action-primary"
              onClick={handleExportData}
              disabled={isLoading || selectedExportSheets.length === 0}
              type="button"
            >
              <span className="action-icon">⬇️</span>
              <span className="action-text">
                {allExportSheetsSelected
                  ? 'Export All Data'
                  : selectedExportSheets.length === 0
                    ? 'Select Sheets to Export'
                    : `Export Selected (${selectedExportSheets.length})`}
              </span>
            </button>
          </div>

          <div className="export-sheet-picker" aria-label="Sheets to include in export">
            <div className="export-sheet-picker-header">
              <h4>Sheets to export</h4>
              <div className="export-sheet-picker-actions">
                <button type="button" className="di-link-btn" onClick={selectAllExportSheets}>
                  Select all
                </button>
                <button type="button" className="di-link-btn" onClick={clearExportSheets}>
                  Clear
                </button>
              </div>
            </div>
            <div className="export-sheet-grid">
              {availableExportSheets.map((sheetName) => {
                const checked = selectedExportSheets.includes(sheetName);
                const inputId = `export-sheet-${sheetName.replace(/\s+/g, '-').toLowerCase()}`;
                return (
                  <label key={sheetName} className="export-sheet-option" htmlFor={inputId}>
                    <input
                      id={inputId}
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleExportSheet(sheetName)}
                    />
                    <span>{sheetName}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <p className="di-import-footnote export-help-note">
            {user?.role === 'superadmin'
              ? 'Exports the sheets you select with current records. Files include an Export Info cover sheet (org, location scope, export time); data sheets stay import-compatible. Downloads are timestamped.'
              : 'Exports selected Users and/or Devices for your location. Files include an Export Info cover sheet (org, location, export time); data sheets stay import-compatible. Downloads are timestamped.'}
          </p>
        </div>
      )}

      {/* Comprehensive Reference Modal */}
      <Modal
        isOpen={showReferenceModal}
        onClose={() => setShowReferenceModal(false)}
        title={user?.role === 'superadmin' ? '📖 Master Data Import Guide' : '📖 Location Data Import Guide'}
        size="large"
      >
        <div className="di-guide">
          <div className="di-tabs di-tabs--in-modal di-tabs--wrap" role="tablist" aria-label="Import guide sections">
            <button
              type="button"
              role="tab"
              aria-selected={guideTab === 'overview'}
              className={`di-tab-btn ${guideTab === 'overview' ? 'active' : ''}`}
              onClick={() => setGuideTab('overview')}
            >
              Overview
            </button>
            {guideSheetNames.map((name) => (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={guideTab === name}
                className={`di-tab-btn ${guideTab === name ? 'active' : ''}`}
                onClick={() => setGuideTab(name)}
              >
                {name}
              </button>
            ))}
            <button
              type="button"
              role="tab"
              aria-selected={guideTab === 'rules'}
              className={`di-tab-btn ${guideTab === 'rules' ? 'active' : ''}`}
              onClick={() => setGuideTab('rules')}
            >
              Rules
            </button>
          </div>

          <div className="di-guide-panel">
            {guideTab === 'overview' && (
              <div className="di-ref-body">
                <div className={`di-callout ${user?.role === 'superadmin' ? 'di-callout--info' : 'di-callout--success'}`}>
                  <h4>{user?.role === 'superadmin' ? 'System administrator' : 'Location administrator'}</h4>
                  <p>
                    {user?.role === 'superadmin'
                      ? 'You manage all organizational master data. Import sheets in order so references stay valid.'
                      : 'You manage users and devices for your assigned location. Import Users first, then Devices.'}
                  </p>
                  {user?.role === 'admin' && roleGuideContent?.locationNotes && (
                    <p className="di-callout-extra">{roleGuideContent.locationNotes}</p>
                  )}
                </div>

                {roleGuideContent?.importOrderTips && (
                  <div className="di-callout di-callout--info">
                    <h4>Import order</h4>
                    <p>{roleGuideContent.importOrderTips}</p>
                    {renderImportOrderSteps(roleGuideContent?.importOrderSteps)}
                    {renderSelectiveImportDeps(roleGuideContent?.selectiveImportDeps)}
                  </div>
                )}

                {roleGuideContent?.keyRestrictions?.length > 0 && (
                  <div className="di-callout di-callout--warning">
                    <h4>Important restrictions</h4>
                    <ul>
                      {roleGuideContent.keyRestrictions.map((restriction, idx) => (
                        <li key={idx}>{restriction}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {roleGuideContent?.keyCapabilities?.length > 0 && (
                  <div className="di-callout di-callout--success">
                    <h4>What you can do</h4>
                    <ul>
                      {roleGuideContent.keyCapabilities.map((cap, idx) => (
                        <li key={idx}>{cap}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {roleGuideContent?.keyReminders?.length > 0 && (
                  <div className="di-callout di-callout--neutral">
                    <h4>Key reminders</h4>
                    <div className="di-reminder-grid">
                      {roleGuideContent.keyReminders.map((group) => (
                        <div key={group.title} className="di-reminder-card">
                          <strong>{group.title}</strong>
                          <ul>
                            {group.items.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    {roleGuideContent?.rulesNote && (
                      <p className="di-callout-extra">{roleGuideContent.rulesNote}</p>
                    )}
                  </div>
                )}

                <p className="di-guide-next">
                  Open a sheet tab for column details
                  {guideSheetNames.length > 0 && (
                    <>
                      {' '}(e.g.{' '}
                      <button type="button" className="di-browse-link" onClick={() => setGuideTab(guideSheetNames[0])}>
                        {guideSheetNames[0]}
                      </button>
                      )
                    </>
                  )}
                  , or <button type="button" className="di-browse-link" onClick={() => setGuideTab('rules')}>Rules</button> for matching and validation.
                </p>
              </div>
            )}

            {guideSheetNames.includes(guideTab) && (
              <div className="di-ref-body">
                {renderGuideSheetFields(guideTab)}
              </div>
            )}

            {guideTab === 'rules' && (
              <div className="di-ref-body">
                <div className="di-callout di-callout--success">
                  <h4>Uniqueness & field requirements</h4>
                  <ul>
                    {user?.role === 'superadmin' ? (
                      <>
                        <li><strong>Device Names:</strong> name (unique per org), code (unique per org)</li>
                        <li><strong>Departments:</strong> name (unique per org), code (unique per org)</li>
                        <li><strong>Locations:</strong> location_code (unique per org), name (not unique)</li>
                        <li><strong>Complaint Categories:</strong> code (unique per org); system Other is not exported; cannot be deactivated or narrowed via import</li>
                        <li><strong>Users references:</strong> department_name (comma-separated) and location_code</li>
                        <li><strong>Devices:</strong> Dates YYYY-MM-DD; numbers without symbols; Yes/No accepts Yes, No, true, false, 1, 0</li>
                        <li><strong>Defaults:</strong> Location max_users 50, max_devices 1000 if omitted</li>
                      </>
                    ) : (
                      <>
                        <li><strong>Users:</strong> Scoped to your location. department_name may be comma-separated. Departments must already exist.</li>
                        <li><strong>Devices:</strong> Scoped to your location. Device Names and Departments must exist. Custodian / technicians use employee IDs.</li>
                        {employeeIdMode === 'auto' && (
                          <li><strong>Auto employee IDs:</strong> Import Users first, export to get IDs, then import Devices that reference them.</li>
                        )}
                        <li><strong>Location is fixed:</strong> Your location ({user?.location_name || 'assigned location'}) cannot be changed on existing devices.</li>
                        <li><strong>Dates:</strong> YYYY-MM-DD · <strong>Yes/No:</strong> Yes, No, true, false, 1, 0</li>
                      </>
                    )}
                  </ul>
                </div>

                <div className="di-callout di-callout--warning">
                  <h4>Entity matching & validation</h4>
                  <ul>
                    {user?.role === 'superadmin' ? (
                      <>
                        <li><strong>Exact name match</strong> (case-sensitive) links to an existing entity when found.</li>
                        <li>Missing references return an error — create them first or include them in the upload.</li>
                        <li><strong>Device serial_number</strong> must be unique (duplicates update, not insert).</li>
                        <li>Spell department, location, and device names consistently.</li>
                      </>
                    ) : (
                      <>
                        <li><strong>Departments & device names</strong> must already exist — ask your System Administrator if missing.</li>
                        <li><strong>Custodian & technicians</strong> use unique employee IDs; technicians must be active in your location and the device department.</li>
                        {employeeIdMode === 'auto' && (
                          <li><strong>New users:</strong> Export Users after import to get generated employee IDs for the Devices sheet.</li>
                        )}
                        <li>All imports are assigned to your location ({user?.location_name || 'assigned location'}).</li>
                        <li><strong>Device serial_number</strong> must be unique (duplicates update, not insert).</li>
                        <li>Device location cannot change after creation.</li>
                      </>
                    )}
                  </ul>
                </div>

                <div className="di-callout di-callout--info">
                  <h4>Template Instructions sheet</h4>
                  <p>
                    The downloaded Excel template includes an Instructions sheet with this field guide, examples, and import order.
                  </p>
                </div>

                {user?.role === 'admin' && (
                  <div className="di-callout di-callout--neutral">
                    <h4>Need master data created?</h4>
                    <p>
                      Contact your System Administrator to create Departments or Device Names before you import users and devices.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Unified Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={closeImportModal}
        onBackdropClick={() => {}}
        title={
          importModalState === 'file-selection' && !previewData ? '📥 Import Master Data'
            : importModalState === 'file-selection' ? '📥 Review Import'
              : importModalState === 'progress' ? '⏳ Processing Import…'
                : '📊 Import Results'
        }
        size="large"
        footer={
          importModalState === 'file-selection' && !previewData ? (
            <button type="button" className="action-btn action-secondary" onClick={closeImportModal}>
              <span className="action-text">Cancel</span>
            </button>
          ) : importModalState === 'file-selection' && previewData ? (
            <>
              <button type="button" className="action-btn action-secondary" onClick={closeImportModal}>
                <span className="action-text">Cancel</span>
              </button>
              <button
                type="button"
                className="action-btn action-primary"
                onClick={confirmImport}
                disabled={!previewData?.isValid}
              >
                <span className="action-text">Proceed to Import</span>
              </button>
            </>
          ) : importModalState === 'results' && importResults ? (
            <button type="button" className="action-btn action-primary" onClick={closeImportModal}>
              <span className="action-text">Done</span>
            </button>
          ) : null
        }
      >
        {importModalState === 'file-selection' && !previewData ? (
          <div className="di-import-stack">
            <p className="di-import-lead">
              Choose an Excel file (.xlsx) built from the template.
            </p>
            <div
              className="di-dropzone"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="di-dropzone-icon" aria-hidden="true">📁</div>
              <p className="di-dropzone-prompt">
                Drop file here, or{' '}
                <button type="button" className="di-browse-link" onClick={openFileBrowser}>
                  browse
                </button>
              </p>
              <p className="di-dropzone-hint">.xlsx or .xls · max 10 MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="di-hidden-file-input"
              />
            </div>
            <p className="di-import-footnote">
              Download the template first if you need the correct sheet names and columns.
              Field rules and restrictions are in View Detailed Reference.
            </p>
          </div>
        ) : importModalState === 'progress' && importProgress ? (
          <div className="di-progress-panel">
            <div className="di-dropzone-icon" aria-hidden="true">📊</div>
            <h4>{importProgress.message}</h4>
            <p>Please wait while we process your data…</p>
            {importProgress.progress !== undefined && (
              <>
                <div className="di-progress-track">
                  <div
                    className={`di-progress-fill${importProgress.status === 'processing' ? ' is-busy' : ''}`}
                    style={{ width: `${importProgress.progress}%` }}
                  />
                </div>
                <div className="di-progress-label">
                  {importProgress.progress}%{importProgress.status === 'processing' ? ' …processing' : ''}
                </div>
              </>
            )}
          </div>
        ) : importModalState === 'results' && importResults ? (
          <div>
            {importResults.summary && (() => {
              const { created, updated, errors } = importResults.summary;
              const hasSuccess = created > 0 || updated > 0;
              let tone = 'error';
              let text = 'Import Failed';
              let subtext = 'No records were processed. Please check the errors below.';
              if (hasSuccess && errors === 0) {
                tone = 'success';
                text = 'Import Successful';
                subtext = 'All records processed successfully.';
              } else if (hasSuccess && errors > 0) {
                tone = 'warning';
                text = 'Import Completed with Errors';
                subtext = `${created + updated} records successfully processed, but ${errors} error${errors > 1 ? 's' : ''} occurred.`;
              }
              return (
                <div className={`di-status-banner di-status-banner--${tone}`}>
                  <span aria-hidden="true">{tone === 'success' ? '✅' : tone === 'warning' ? '⚠️' : '❌'}</span>
                  <div>
                    <h4>{text}</h4>
                    <p>{subtext}</p>
                  </div>
                </div>
              );
            })()}

            <div className="di-stat-grid">
              <div className="di-stat-card di-stat-card--created">
                <div className="di-stat-label">Created</div>
                <div className="di-stat-value">{importResults.summary.created}</div>
              </div>
              <div className="di-stat-card di-stat-card--updated">
                <div className="di-stat-label">Updated</div>
                <div className="di-stat-value">{importResults.summary.updated}</div>
              </div>
              <div className={`di-stat-card di-stat-card--errors${importResults.summary.errors > 0 ? '' : ' is-zero'}`}>
                <div className="di-stat-label">Errors</div>
                <div className="di-stat-value">{importResults.summary.errors}</div>
              </div>
            </div>

            {importResults.download_url && (
              <div className="di-download-row">
                <div>
                  <strong>Detailed results available</strong>
                  <p>Download the Excel report with row-by-row Status and Reason columns.</p>
                </div>
                <button
                  type="button"
                  className="action-btn action-primary"
                  onClick={() => handleDownloadResults(importResults.download_url)}
                  disabled={isLoading}
                >
                  <span className="action-icon">⬇️</span>
                  <span className="action-text">Download Excel</span>
                </button>
              </div>
            )}

            {importResults.details && Object.keys(importResults.details).length > 0 && (
              <div className="di-entity-section">
                <h4 className="di-section-title">Results by entity</h4>
                <div className="di-entity-table" role="table" aria-label="Results by entity">
                  <div className="di-entity-row di-entity-row--head" role="row">
                    <span className="di-entity-cell di-entity-cell--name" role="columnheader">Entity</span>
                    <span className="di-entity-cell" role="columnheader">Created</span>
                    <span className="di-entity-cell" role="columnheader">Updated</span>
                    <span className="di-entity-cell" role="columnheader">Errors</span>
                  </div>
                  {Object.entries(importResults.details).map(([entity, data]) => {
                    const label = entity.charAt(0).toUpperCase() + entity.slice(1);
                    const isEmpty = data.status === 'empty';
                    const hasErrors = !isEmpty && data.errors > 0;
                    return (
                      <div
                        key={entity}
                        className={`di-entity-row${hasErrors ? ' di-entity-row--error' : ''}${isEmpty ? ' di-entity-row--empty' : ''}`}
                        role="row"
                      >
                        <span className="di-entity-cell di-entity-cell--name" role="cell">{label}</span>
                        {isEmpty ? (
                          <span className="di-entity-cell di-entity-cell--skip" role="cell">
                            Empty (skipped)
                          </span>
                        ) : (
                          <>
                            <span className="di-entity-cell di-entity-metric di-entity-metric--created" role="cell">
                              {data.created}
                            </span>
                            <span className="di-entity-cell di-entity-metric di-entity-metric--updated" role="cell">
                              {data.updated}
                            </span>
                            <span
                              className={`di-entity-cell di-entity-metric di-entity-metric--errors${hasErrors ? '' : ' is-zero'}`}
                              role="cell"
                            >
                              {data.errors || 0}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {importResults.warnings?.length > 0 && (
              <div className="di-callout di-callout--warning">
                <h5>Import notices</h5>
                <ul>
                  {importResults.warnings.map((warning, idx) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {importResults.errors?.length > 0 && (
              <div className="di-callout di-callout--danger">
                <h5>Error details</h5>
                <div className="di-error-list">
                  {importResults.errors.map((error, index) => (
                    <div key={index}>
                      {error.row != null ? (
                        <span className="row-label">Row {error.row}: </span>
                      ) : error.sheet ? (
                        <span className="row-label">Sheet {error.sheet}: </span>
                      ) : null}
                      <span>{error.message}</span>
                      {error.issues?.length > 0 && (
                        <ul>
                          {error.issues.map((iss, j) => (
                            <li key={j}>
                              {iss.field ? <><code>{iss.field}</code>{': '}</> : null}
                              {iss.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : importModalState === 'file-selection' && previewData ? (
          <>
            <div className="di-meta-grid">
              <div>
                <label>File Name</label>
                <div className="value">{previewData.fileName}</div>
              </div>
              <div>
                <label>File Size</label>
                <div className="value">{previewData.fileSize} KB</div>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label>Last Modified</label>
                <div className="value">{previewData.lastModified}</div>
              </div>
            </div>

            <div>
              {previewData.isValid && !previewData.hasRowLevelFindings && (
                <div className="di-callout di-callout--success">
                  <h5>
                    {previewData.sheetsWithData === 1
                      ? 'Template structure OK. 1 sheet with data will be sent to the server.'
                      : `Template structure OK. ${previewData.sheetsWithData} sheets with data will be sent to the server.`}
                  </h5>
                </div>
              )}

              {!previewData.isValid && previewData.allRowsFailedPreValidation && (
                <div className="di-callout di-callout--danger">
                  <h5>All rows have issues — import blocked</h5>
                  <p style={{ marginBottom: '0.75rem' }}>
                    Every record failed the local pre-check. Download the pre-validation report, fix the rows, and try again.
                  </p>
                  <button type="button" className="action-btn action-secondary" onClick={handleDownloadPreValidation}>
                    <span className="action-text">Download pre-validation (Excel)</span>
                  </button>
                </div>
              )}

              {previewData.isValid && previewData.hasRowLevelFindings && (
                <div className="di-callout di-callout--warning">
                  <h5>You can still import</h5>
                  <p>
                    {previewData.totalRowIssueCount} local row-level issue(s) found. The server will re-validate and return all issues per row.
                  </p>
                </div>
              )}

              {!previewData.isValid && (previewData.validationErrors.length > 0 || previewData.sheetsWithData === 0) && (
                <div className="di-callout di-callout--danger">
                  <h5>Template / file issues — import blocked</h5>
                  {previewData.validationErrors.length > 0 && (
                    <ul>
                      {previewData.validationErrors.map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  )}
                  {previewData.sheetsWithData === 0 && previewData.validationErrors.length === 0 && (
                    <p>No data rows found. Add at least one row of data in a sheet, or use the downloaded template.</p>
                  )}
                </div>
              )}

              {((previewData.isValid && previewData.hasRowLevelFindings) || previewData.allRowsFailedPreValidation) && (
                <div className="di-callout di-callout--info">
                  <div className="di-inline-actions">
                    <strong>Pre-validation summary</strong>
                    <button type="button" className="action-btn action-secondary" onClick={handleDownloadPreValidation}>
                      <span className="action-text">Download pre-validation (Excel)</span>
                    </button>
                  </div>
                  {previewData.duplicateWarnings?.length > 0 && (
                    <ul>
                      {previewData.duplicateWarnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  )}
                  {previewData.preValidationSummary?.totals && (
                    <div className="di-preview-stats">
                      <div className="di-preview-stat">
                        <div className="lbl">Records checked</div>
                        <div className="val">{previewData.preValidationSummary.totals.total}</div>
                      </div>
                      <div className="di-preview-stat">
                        <div className="lbl">Passed</div>
                        <div className="val">{previewData.preValidationSummary.totals.passed}</div>
                      </div>
                      <div className="di-preview-stat">
                        <div className="lbl">Failed</div>
                        <div className="val">{previewData.preValidationSummary.totals.failed}</div>
                      </div>
                    </div>
                  )}

                  {previewData.rowIssueTypeBreakdown?.total && (() => {
                    const t = previewData.rowIssueTypeBreakdown.total || {};
                    const parts = [];
                    if ((t.Required || 0) > 0) parts.push(`Missing required fields (${t.Required})`);
                    if ((t.Duplicate || 0) > 0) parts.push(`Duplicate values in this file (${t.Duplicate})`);
                    if ((t['Role-rule'] || 0) > 0) parts.push(`Role/department rules (${t['Role-rule']})`);
                    if ((t.Other || 0) > 0) parts.push(`Other (${t.Other})`);
                    if (parts.length === 0) return null;
                    return (
                      <p><strong>Issue summary:</strong> {parts.join(' · ')}</p>
                    );
                  })()}

                  {previewData.distinctIssueSummary?.top?.length > 0 && (
                    <div>
                      <strong>Top issue(s)</strong>
                      <ul>
                        {previewData.distinctIssueSummary.top.map((it, idx) => (
                          <li key={idx}>{it.message} ({it.count})</li>
                        ))}
                      </ul>
                      {previewData.distinctIssueSummary.remainder > 0 && (
                        <p className="muted">… and {previewData.distinctIssueSummary.remainder} more (see download).</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {previewData.ignoredSheetsWarnings?.length > 0 && (
                <div className="di-callout di-callout--warning">
                  <h5>Sheets present but will be ignored</h5>
                  <ul>
                    {previewData.ignoredSheetsWarnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {previewData.missingSheetsWarnings?.length > 0 && (
                <div className="di-callout di-callout--neutral">
                  <h5>Sheets not found in this file</h5>
                  <ul>
                    {previewData.missingSheetsWarnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {previewData.validationWarnings?.length > 0 && (
                <div className="di-callout di-callout--warning">
                  <h5>Sheet notices</h5>
                  <ul>
                    {previewData.validationWarnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div>
              <h4 className="di-section-title">Sheets found ({previewData.sheets.length})</h4>
              <div className="di-sheets-list">
                {previewData.sheets.map((sheet, idx) => {
                  const statusClass = sheet.status === 'has-data'
                    ? 'is-data'
                    : sheet.status === 'empty'
                      ? 'is-empty'
                      : 'is-error';
                  return (
                    <div key={idx} className={`di-sheet-found ${statusClass}`}>
                      <div className="di-sheet-found-top">
                        <strong>{sheet.name}</strong>
                        <span className="di-row-badge">{sheet.rowCount} rows</span>
                      </div>
                      <div className="di-sheet-found-cols">
                        {sheet.columns.length} columns: {sheet.columns.join(', ')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
}

export default DataImportPage;
